import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import {deepAssign, hexToRgba} from "../utils";
import {nn} from "date-fns/locale";

const htmlLegendPlugin = {
	id: 'htmlLegend',
	afterUpdate( chart, args, options ) {
		const $legends = $( options.container );
		$legends.empty();

		const items = chart.options.plugins.legend.labels.generateLabels( chart );
		items.forEach( function( item, index ) {
			var color = item.fillStyle;
			if ( color instanceof CanvasGradient ) {
				color = chart.config.data.datasets[ index ].borderColor;
			}
			$legends.append( `
<li class="flex items-center gap-2">
	<span class="inline-block rounded-full aspect-square" style="width: 16px; height: 16px; background: ${color}"></span>
	<span class="top-0.5">${item.text}</span>
</li>
` );
		} );
	},
};

const chartAreaBorder = {
	id: 'chartAreaBorder',
	beforeDraw( chart, args, options ) {
		const {
			ctx,
			chartArea: {
				left,
				top,
				width,
				height,
			},
		} = chart;
		ctx.save();
		ctx.strokeStyle = options.borderColor;
		ctx.lineWidth = options.borderWidth;
		ctx.setLineDash( options.borderDash || [] );
		ctx.lineDashOffset = options.borderDashOffset;
		ctx.strokeRect( left, top, width, height );
		ctx.restore();
	},
};

window.createCharts = function( canvas, type, data, settings ) {
	Chart.defaults.font.family = 'hedley-new-web';
	Chart.defaults.color = '#00123b';
	Chart.defaults.font.size = 16;

	var plugins = [chartAreaBorder];
	var options = {
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				backgroundColor: 'rgba(0, 18, 59, .8)',
				padding: 10,
				caretSize: 0,
				boxWidth: 12,
				boxHeight: 12,
				boxPadding: 5,
				usePointStyle: true,
				titleFont: { weight: '400' },
				titleMarginBottom: 3,
				callbacks: {
					labelColor: function( context ) {
						var color = context.dataset.backgroundColor;
						if ( color instanceof Function ) {
							color = context.dataset.borderColor;
						} else if ( Array.isArray( color ) ) {
							color = color[ context.dataIndex ];
						}
						return {
							borderColor: color,
							backgroundColor: color,
							borderWidth: 0,
							borderRadius: 0,
						};
					},
					label: function( context ) {
						var result = context.formattedValue;
						if ( settings.percent ) {
							result += ' %';
						}
						return result;
					},
				},
			},
			chartAreaBorder: {
				borderColor: 'rgba(0,0,0,0)',
			},
		},
		layout: {
			padding: 0,
		},
		responsive: true,
		elements: {
			arc: {
				borderWidth: 0,
			},
			point: {
				hitRadius: 4,
				hoverRadius: 4,
				radius: 0,
			},
			line: {
				borderWidth: 2,
			},
		},
		hover: { mode: null },
		borderSkipped: 'middle',
	};

	if ( settings.legend ) {
		plugins.push( htmlLegendPlugin );
		deepAssign( options, {
			plugins: {
				htmlLegend: {
					container: document.getElementById( settings.legend.id ),
				},
			},
		} );
	}

	if ( type === 'bar' ) {
		deepAssign( options, {
			borderRadius: 3,
		} );
	}

	if ( type === 'stacked-bar' ) {
		type = 'bar';
		deepAssign( options, {
			scales: {
				x: {
					stacked: true,
				},
				y: {
					stacked: true,
				},
			},
			borderRadius: 5,
		} );
	}

	if ( type !== 'pie' ) {
		deepAssign( options, {
			scales: {
				x: {
					border: {
						display: false,
					},
					ticks: {
						display: settings.showXAxisLabel,
						color: '#7d879f',
					},
					grid: {
						display: false,
					},
				},
				y: {
					border: {
						display: false,
					},
					ticks: {
						display: settings.showYAxisLabel,
						color: '#7d879f',
						callback: function( value, index, values ) {
							return value + settings.yAxisSuffix + ' ';
						},
					},
					grid: {
						display: settings.showYAxisLines,
						tickLength: 0,
						color: '#dce0e7',
					},
				},
			},
		} );

		if ( data.labels.every( function( label ) {
			return typeof label === 'string' && label.match( /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/ );
		} ) ) {
			deepAssign( options, {
				scales: {
					x: {
						type: 'time',
						time: {
							tooltipFormat: 'd. MMM yyyy',
							displayFormats: {
								year: 'yyyy',
								quarter: "'Kv' Q yyyy",
								month: 'MMM yyyy',
							},
						},
						adapters: {
							date: {
								locale: nn,
							},
						},
					},
				},
			} );

			let timeAxisUnit = null;
			switch ( settings.xAxisType ) {
				case 'yearly':
					timeAxisUnit = 'year';
					break;
				case 'quarterly':
					timeAxisUnit = 'quarter';
					break;
				case 'monthly':
					timeAxisUnit = 'month';
					break;
			}
			if ( timeAxisUnit ) {
				deepAssign( options, {
					scales: {
						x: {
							time: {
								unit: timeAxisUnit,
							},
						},
					},
				} );
			}

		}
	}

	if ( type === 'line' ) {
		deepAssign( options, {
			plugins: {
				tooltip: {
					mode: 'index',
				},
			},
		} );
		data.datasets.forEach( function( dataset ) {
			dataset.fill = 'start';
			var color = dataset.backgroundColor;
			dataset.borderColor = color;
			dataset.backgroundColor = function( context ) {
				var chart = context.chart;
				var ctx = chart.ctx;
				var chartArea = chart.chartArea;

				if ( !chartArea ) {
					return; // This case happens on initial chart load
				}

				var gradient = ctx.createLinearGradient( 0, chartArea.top, 0, chartArea.top + chartArea.bottom );
				gradient.addColorStop( 0, hexToRgba( color, 0.32 ) );
				gradient.addColorStop( 0.5, hexToRgba( color, 0.32 ) );
				gradient.addColorStop( 1, hexToRgba( color, 0 ) );
				return gradient;
			};
		} );
	}

	new Chart( canvas, {
		type: type,
		data: data,
		options: options,
		plugins: plugins,
	} );
};
